import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { selectResultsError } from "features/Model/Review/Results/_ducks/selectors";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { categories } from "utils/enclosure";
import { getF280SummaryData } from "utils/results/api";
import { hasInsightsAccess, hasRxRResultsAccess } from "utils/insights";
import { getF280DesignHeatingTemp } from "utils/weather";

import Select from "components/Input/Select";
import Button from "components/Button";
import ExportF280PDF from "features/Model/Review/F280Summary/ExportF280PDF";
import LoadingDots from "components/LoadingDots";
import Checkbox from "components/Input/Checkbox";
import RoomByRoomResults from "./RoomByRoomResults";
import { RoomByRoomErrorSummary } from "features/Model/Review/F280Summary";

import classes from "./style.module.scss";
import resultsClasses from "features/Model/Review/Results/style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import Export from "assets/images/icons/JSX/Export";

const { getResults } = resultsActions;

export const Stat = ({ label = "", value = 0, unit = "", decimals = 2 }) => (
    <div className={classes.stat}>
        <p>{label}:</p>
        <span>
            {unit === "$" && unit}
            <strong>{typeof value === "number" ? value.toFixed(decimals).replace("-0.00", "0.00") : value}</strong>{" "}
            {unit !== "$" && unit && unit}
        </span>
    </div>
);

export const OutdoorTempStat = ({ label = "", tempUnit = "", heatLoss, dhdbt, displayUnits }) => {
    const [outdoorTemp, setOutdoorTemp] = useState(-5);
    const [outdoorTempInput, setOutdoorTempInput] = useState("-5");

    // Calculate interpolated heat loss based on outdoor temperature
    const calculateInterpolatedHeatLoss = (temp) => {
        if (!heatLoss || !dhdbt) return 0;

        // Linear interpolation between (dhdbt, heatLoss) and (16, 0)
        // Heat loss is 0 at 16°C
        if (temp >= 16) return 0;
        // if (temp <= dhdbt) return heatLoss;

        // Linear interpolation formula: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
        const interpolatedValue = (heatLoss * (16 - temp)) / (16 - dhdbt);
        return Math.max(0, interpolatedValue);
    };

    const interpolatedHeatLoss = calculateInterpolatedHeatLoss(outdoorTemp);

    return (
        <div className={classes.stat}>
            <p>
                {label}{" "}
                <input
                    type="number"
                    value={outdoorTempInput}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        // Allow user to type freely (including "-", "-1", etc.)
                        setOutdoorTempInput(inputValue);
                        // Update numeric state if we have a valid number
                        const parsed = parseFloat(inputValue);
                        if (!isNaN(parsed) && inputValue !== "" && inputValue !== "-") {
                            setOutdoorTemp(parsed);
                        }
                    }}
                    onBlur={(e) => {
                        // Clamp and validate the value when user leaves the field
                        const parsed = parseFloat(e.target.value);
                        const min = Math.min(dhdbt, -22) || -40;
                        if (!isNaN(parsed)) {
                            const clamped = Math.min(Math.max(parsed, min), 16);
                            setOutdoorTemp(clamped);
                            setOutdoorTempInput(clamped.toString());
                        } else {
                            // If invalid, reset to current valid value
                            const clamped = Math.min(Math.max(outdoorTemp, min), 16);
                            setOutdoorTemp(clamped);
                            setOutdoorTempInput(clamped.toString());
                        }
                    }}
                    style={{
                        width: "50px",
                        padding: "0.125rem 0.25rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.25rem",
                        fontSize: "0.75rem",
                        backgroundColor: "#ffffff",
                        textAlign: "center",
                    }}
                    min={Math.min(dhdbt, -22) || -40}
                    max={16}
                    step={1}
                />
                {` ${tempUnit}:`}
            </p>
            <span>
                <strong>
                    {typeof (displayUnits === "Metric" ? interpolatedHeatLoss : interpolatedHeatLoss * 3.41214) ===
                    "number"
                        ? (displayUnits === "Metric" ? interpolatedHeatLoss : interpolatedHeatLoss * 3.41214)
                              .toFixed(0)
                              .replace("-0.00", "0.00")
                        : (displayUnits === "Metric" ? interpolatedHeatLoss : interpolatedHeatLoss * 3.41214) || "N/A"}
                </strong>{" "}
                {displayUnits === "Metric" ? "W" : "Btu/h"}
            </span>
        </div>
    );
};

const RoomByRoom = ({ getResults, modelSave }) => {
    const resultsError = useSelector(selectResultsError);

    const [exporting, toggleExporting] = useState(false);
    const [upgradeSelected, toggleUpgradeSelected] = useState("");
    const [exportUnits, toggleExportUnits] = useState("");
    const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);
    const [exportData, setExportData] = useState(false);
    const [includeLatent, setIncludeLatent] = useState(true);

    const { packages = {} } = useStateSelector("upgrades") || {};

    const upgradeExportOpts = Object.keys(packages)
        .filter((pkg) => pkg !== "base")
        .map((pkg) => ({
            value: pkg,
            label: packages[pkg].name,
        }));

    const {
        modelId,
        modelDetails = {},
        modelData: {
            location: { weather: { f280: { location: { id: f280LocationId = 0 } = {} } = {} } = {} } = {},
            uiSettings: { primaryUnits: modelUnits = {} } = {},
            specifications: { numStoreys: { id: numStoreysId } = {} } = {},
        } = {},
    } = useStateSelector("model") || {};

    const { name, notes, isSimulationRunning = false, lastCliRun: { cliId: clidIds = {} } = {} } = modelDetails || {};

    const dhdbt = getF280DesignHeatingTemp(f280LocationId);

    const {
        packageResults: packageResultsData = {},
        loading = false,
        error = false,
    } = useStateSelector("results") || {};

    const {
        name: userName = "",
        email = "",
        userMeta: { company = "" } = {},
        userPreferences: {
            companyPhone = "",
            province: { id: provinceId } = {},
            companyAddress = "",
            companyCity = "",
            companyPostalCode = "",
            companyFax = "",
        } = {},
        isUserVerified = true,
    } = useStateSelector("user") || {};

    const { default: { runId: defaultId = "" } = {}, ...rest } = clidIds;
    const cliId = defaultId || rest[Object.keys(rest)[0]]?.runId;

    const { base: { metadata: { mode } = {} } = {} } = packageResultsData || {};

    const storeyCategories = categories(numStoreysId);

    let cityProvinceLabel = "";
    if (companyCity != "") {
        cityProvinceLabel = companyCity;
    }
    if (provinceId != null) {
        const provLabel =
            getOptions({ fieldKey: "weatherRegion", version: "v11-11" }).find((opt) => opt?.value?.id === provinceId)
                ?.label || "";

        cityProvinceLabel = `${cityProvinceLabel}${cityProvinceLabel !== "" ? ", " : ""}${provLabel}`;
    }

    const calcPerformedBy = {
        userName,
        company,
        companyAddress,
        cityProvince: cityProvinceLabel,
        companyPostalCode,
        companyPhone,
        companyFax,
        email,
    };

    const displayUnits = exportUnits || modelUnits || "Metric";

    const resultsData = packageResultsData?.[upgradeSelected || "base"]?.resultsData || {};
    const caseKey =
        upgradeSelected === ""
            ? Object.keys(resultsData).find((key) => key.includes("BaseCase")) || "generalBaseCase"
            : Object.keys(resultsData).find((key) => key.includes("AllUpgrades")) || "generalAllUpgrades";
    const { [caseKey]: { f280Results = {} } = {} } = resultsData;

    const {
        coolingOverrideSetPoint,
        heatLoss,
        heatingOverrideSetPoint,
        nanLabels = [],
        latHeatGain,
        sensHeatGain,
        summaryDetails: { components: summaryComponents = {} } = {},
        roomByRoomResults = {},
        roomByRoomErrors,
    } = f280Results || {};

    // Legacy results predate roomByRoomErrors; an explicit empty array means a new scan found no issues.
    // Ignore the empty default object from a missing caseKey path so we don't falsely show "legacy".
    const hasLoadedF280Results = !isEmpty(f280Results);
    const isLegacyRoomByRoomScan = hasLoadedF280Results && roomByRoomErrors === undefined;
    const resolvedRoomByRoomErrors = roomByRoomErrors || [];

    const hasRxRResults = !isEmpty(roomByRoomResults);
    const hasResults = !!mode;

    const nanComponentMessage = isEmpty(nanLabels)
        ? null
        : `Some components have produced calculation errors. Please check the inputs for the following component(s) and make corrections where required, ensuring no fields are left blank: ${nanLabels.join(
              ", "
          )}`;

    const handleExport = async () => {
        toggleExporting(true);
        //handled in an action and cannot yet be disabled at the source, like handleSubmit
        // if (!readOnly) {
        //     await saveDrawingData();
        // }

        await modelSave();

        const { data: returnData = {} } = await getF280SummaryData({
            modelId,
            upgradePackageId: upgradeSelected,
        });

        setIsPdfExportOpen(true);
        setExportData(returnData);

        toggleExporting(false);
    };

    useEffect(() => {
        if (!hasResults && Object.keys(clidIds).length > 0 && resultsError !== "Results not found.") {
            const runIds = Object.values(clidIds).map(({ runId = "" }) => runId);
            // Get most recent cli results
            getResults({ cliIds: runIds, modelId });
        }
    }, [clidIds, hasResults, modelId]);

    // Errors should remain visible even before Insights is activated (see release notes).
    // Full results tables / export stay gated behind access.
    const hasAccess = hasInsightsAccess(modelDetails) || hasRxRResultsAccess(modelDetails);
    const showErrorSummary = hasLoadedF280Results || !isEmpty(resolvedRoomByRoomErrors) || isLegacyRoomByRoomScan;

    return (
        <div className={classes.section}>
            <div>
                {loading && !isSimulationRunning ? (
                    <LoadingDots className={classes.dots} />
                ) : hasResults ? (
                    <div className={classes.summaryHeader}>
                        <div>
                            <h4>Room-by-room</h4>
                        </div>
                        {hasAccess && (
                            <div>
                                <Button
                                    smallPadding
                                    onClick={handleExport}
                                    disabled={!isUserVerified || exporting || !isEmpty(nanLabels)}
                                    icon={Export}
                                    className={classes.exportButton}
                                >
                                    {exporting ? "Downloading..." : "Download F280 Report"}
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={resultsClasses.intro}>
                        <p>
                            No results to display. Once ready, click Run New Simulation to see your model's performance
                            (v11.11 onward).
                        </p>
                    </div>
                )}
            </div>
            {isSimulationRunning && (
                <div className={resultsClasses.simulationRunningNotice}>
                    <p>
                        <LoadingDots /> A new simulation is in progress. The results should appear momentarily.
                    </p>
                </div>
            )}
            {cliId && hasResults && !error && (
                <>
                    {hasAccess && (
                        <>
                            {(coolingOverrideSetPoint != null || heatingOverrideSetPoint != null) && (
                                <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                                    <p>
                                        The results below have been generated using indoor set points that do not align
                                        with CSA F280-12. Visit the Mechanicals / Temperatures tab to revert the set
                                        points used for calculations. (Heating set point used:{" "}
                                        {heatingOverrideSetPoint || "22"}˚C. Cooling set point used:{" "}
                                        {coolingOverrideSetPoint || "24"}˚C)
                                    </p>
                                </div>
                            )}
                            {nanComponentMessage && (
                                <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                                    <p>{nanComponentMessage}</p>
                                </div>
                            )}
                            <div className={classes.export}>
                                <Select
                                    className={classes.selectUpgrade}
                                    name="exportIncluded"
                                    options={[
                                        {
                                            value: "",
                                            label: "None",
                                        },
                                        ...upgradeExportOpts,
                                    ]}
                                    label="Upgrade Package" // Included in Export"
                                    placeholder="None"
                                    input={{
                                        value: upgradeSelected,
                                        onChange: (value) => toggleUpgradeSelected(value),
                                    }}
                                    large
                                />
                                <Select
                                    name="exportUnits"
                                    options={[
                                        {
                                            value: "",
                                            label: "Same as Model",
                                        },
                                        {
                                            value: "Metric",
                                            label: "Metric (W, RSI)",
                                        },
                                        {
                                            value: "Imperial",
                                            label: "Imperial (Btu/h, R)",
                                        },
                                    ]}
                                    label="Units" // for Export"
                                    input={{
                                        value: exportUnits,
                                        onChange: (value) => toggleExportUnits(value),
                                    }}
                                    large
                                />

                                <Checkbox
                                    name="includeLatent"
                                    label="Include Latent Multiplier"
                                    input={{
                                        onChange: () => setIncludeLatent((prevState) => !prevState),
                                        value: includeLatent,
                                    }}
                                    large
                                />
                            </div>

                            <div className={classes.heatLossGainSummary}>
                                <h4>Heat Loss/Gain Summary</h4>

                                <div className={classes.row}>
                                    <Stat
                                        label="Design Heat Loss"
                                        value={(displayUnits === "Metric" ? heatLoss : heatLoss * 3.41214) || "N/A"}
                                        unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                                        decimals={0}
                                    />
                                    {dhdbt != null && (
                                        <OutdoorTempStat
                                            label="Heat Loss at Outdoor Temp"
                                            tempUnit={"(°C)"}
                                            heatLoss={heatLoss}
                                            dhdbt={dhdbt}
                                            displayUnits={displayUnits}
                                        />
                                    )}
                                    <Stat
                                        label="Design Heat Gain (Sensible + Latent)"
                                        value={
                                            (displayUnits === "Metric" ? latHeatGain : latHeatGain * 3.41214) || "N/A"
                                        }
                                        unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                                        decimals={0}
                                    />
                                    <Stat
                                        label="Design Heat Gain (Sensible)"
                                        value={
                                            (displayUnits === "Metric" ? sensHeatGain : sensHeatGain * 3.41214) || "N/A"
                                        }
                                        unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                                        decimals={0}
                                    />
                                </div>
                            </div>
                            {!hasRxRResults && (
                                <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                                    <p>
                                        Re-run your simulation to generate a breakdown of heat loss and gain by room.
                                        Room View must be enabled in the Enclosure tab to generate Room-by-room results.
                                    </p>
                                </div>
                            )}
                            {hasRxRResults && (
                                <RoomByRoomResults
                                    className={classes.resultsTableContainer}
                                    summaryComponents={summaryComponents}
                                    roomByRoomResults={roomByRoomResults}
                                    displayUnits={displayUnits}
                                    storeyCategories={storeyCategories}
                                    includeLatent={includeLatent}
                                />
                            )}
                        </>
                    )}
                    {showErrorSummary && (
                        <RoomByRoomErrorSummary
                            roomByRoomErrors={resolvedRoomByRoomErrors}
                            isLegacyScan={isLegacyRoomByRoomScan}
                        />
                    )}
                </>
            )}
            <ExportF280PDF
                isPdfExportOpen={isPdfExportOpen}
                handleClose={() => {
                    setIsPdfExportOpen(false);
                    setExportData({});
                }}
                displayData={exportData}
                name={name}
                notes={notes}
                calcPerformedBy={calcPerformedBy}
                heatLossValue={displayUnits === "Metric" ? heatLoss : heatLoss * 3.41214}
                latHeatGainValue={displayUnits === "Metric" ? latHeatGain : latHeatGain * 3.41214}
                latentMultiplier={includeLatent ? 1.3 : 1}
                heatUnit={displayUnits === "Metric" ? "W" : "Btu/h"}
                roomByRoomResults={roomByRoomResults}
                storeyCategories={storeyCategories}
                displayUnits={displayUnits}
                allowedRoomByRoom={true}
            />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    getResults: ({ cliIds, modelId }) => dispatch(getResults({ cliIds, modelId })),
});

export default connect(null, mapDispatchToProps)(RoomByRoom);
